import { type Change, simplifyChanges } from 'prosemirror-changeset';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import { getAttributionKey } from '../decorations/colorSchemes/attributions';

import { optimizeChanges } from './optimizeChanges';

type AttributionIdentity = {
	key: string | undefined;
	mergeable: boolean;
};

const getAttributionIdentity = (change: Change): AttributionIdentity => {
	const identities = new Set<string | undefined>();

	for (const span of [...change.deleted, ...change.inserted]) {
		identities.add(getAttributionKey(span.data));
	}

	if (identities.size > 1) {
		return { key: undefined, mergeable: false };
	}

	return { key: identities.values().next().value, mergeable: true };
};

/**
 * Simplifies and optimizes consecutive changes without crossing an attribution boundary.
 * A change which already contains more than one identity is deliberately kept isolated.
 */
export const simplifyChangesWithAttribution = (
	changes: readonly Change[],
	doc: PMNode,
): Change[] => {
	const result: Change[] = [];
	let run: Change[] = [];
	let runIdentity: AttributionIdentity | undefined;

	const flush = () => {
		if (run.length > 0) {
			// Some show-diff producers use structurally compatible Change objects rather than the
			// library class. `simplifyChanges` reads runtime `lenA`/`lenB` getters even though they
			// are intentionally omitted from its public declarations, so add equivalent getters
			// without calling the library's internal constructor.
			const normalizedRun: Change[] = run.map((change) => ({
				...change,
				get lenA() {
					return change.toA - change.fromA;
				},
				get lenB() {
					return change.toB - change.fromB;
				},
			}));
			result.push(...optimizeChanges(simplifyChanges(normalizedRun, doc)));
		}
		run = [];
		runIdentity = undefined;
	};

	for (const change of changes) {
		const identity = getAttributionIdentity(change);
		const canJoinRun =
			runIdentity?.mergeable === true && identity.mergeable && runIdentity.key === identity.key;

		if (run.length > 0 && !canJoinRun) {
			flush();
		}

		run.push(change);
		runIdentity = identity;

		if (!identity.mergeable) {
			flush();
		}
	}

	flush();
	return result;
};
